'use client';

import type { Breadcrumb } from '@eliadmin/core/src/api/products/types/product-types';
import { BREADCRUMBS_CONFIG } from '@eliadmin/core/src/constants/breadcrumbs-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  arePathsEqual,
  getFlatBreadcrumbs,
  getSlugFromPage,
} from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { IoIosArrowRoundForward } from 'react-icons/io';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type BreadcrumbsProps = {
  className?: {
    wrapper?: string;
    list?: string;
    item?: string;
    itemActive?: string;
    link?: string;
  };
  homeText?: string;
  breadcrumb: Breadcrumb;
};

export function CustomBreadcrumbs({ breadcrumb, className, homeText }: BreadcrumbsProps) {
  const { t } = useTranslations();
  const pathname = usePathname();

  const breadcrumbs = getFlatBreadcrumbs(breadcrumb);

  if (BREADCRUMBS_CONFIG.noDisplay.filter((el: string) => el === pathname).length) {
    return <nav className="hidden"></nav>;
  }

  return (
    <nav
      className={cn(
        'w-full rounded-none bg-[#13CC92] px-4 py-2 uppercase text-black sm:px-8',
        'ea-breadcrumbs',
        className?.wrapper,
      )}
    >
      <ol
        className={cn(
          'm-auto flex w-full max-w-[1280px] justify-center text-black md:justify-start',
          'ea-breadcrumbs__list',
          className?.list,
        )}
      >
        <li
          className={cn(
            {
              [cn(
                'pointer-events-none font-bold ',
                'ea_breadcrumbs__item--active',
                className?.itemActive,
              )]: arePathsEqual(pathname, getSlugFromPage('home')),
            },
            'ea_breadcrumbs__item',
            className?.item,
          )}
        >
          <Link
            href={getSlugFromPage('home')}
            className={cn('ea-breadcrumbs__link', className?.link)}
            prefetch={false}
          >
            <span className="font-bold">{homeText ?? t('text160')}</span>
          </Link>
        </li>
        {breadcrumbs.map(breadcrumb => {
          const breadcrumbSlug = breadcrumb.slug ?? '';

          return (
            <li key={breadcrumbSlug} className="flex w-auto items-center justify-center">
              <IoIosArrowRoundForward className="h-6 w-6" />
              <span
                className={cn(
                  {
                    [cn(
                      'pointer-events-none text-black',
                      'ea_breadcrumbs__item--active',
                      className?.itemActive,
                    )]: arePathsEqual(pathname, breadcrumbSlug),
                  },
                  'ea_breadcrumbs__item',
                  className?.item,
                )}
              >
                <Link
                  href={breadcrumbSlug}
                  className={cn('ea-breadcrumbs__link font-bold', className?.link)}
                  prefetch={false}
                >
                  {breadcrumb.name}
                </Link>
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
